/**
 * Servicio para obtener el grupo del estudiante desde Google Sheets
 * Basado en GoogleSheetsService.cs del proyecto Unity
 */

// Cache del grupo del estudiante para evitar consultas repetidas
const grupoCache: Map<string, string> = new Map();

// URL del endpoint API Gateway que consulta Google Sheets
const STUDENT_GROUP_API_URL = 'https://of11luqmjb.execute-api.us-east-1.amazonaws.com/production/student-group';

export interface StudentGroupResponse {
  grupo: string;
}

/**
 * Obtiene el grupo del estudiante desde Google Sheets usando su email
 * Utiliza un endpoint Lambda/API Gateway que consulta Google Sheets
 * 
 * @param studentEmail Email del estudiante
 * @returns Promise con el grupo del estudiante o "General" si no se encuentra
 */
export async function getStudentGroup(studentEmail: string): Promise<string> {
  // Validar email
  if (!studentEmail || studentEmail.trim() === '') {
    // console.warn('[GoogleSheetsService] Email del estudiante está vacío. Usando grupo "General" por defecto.');
    return 'General';
  }

  // Normalizar email
  const normalizedEmail = studentEmail.trim().toLowerCase();

  // Verificar cache
  if (grupoCache.has(normalizedEmail)) {
    const grupo = grupoCache.get(normalizedEmail)!;
    // console.log(`[GoogleSheetsService] Grupo obtenido desde cache: ${grupo}`);
    return grupo;
  }

  // Construir URL con el email como query parameter
  const requestUrl = `${STUDENT_GROUP_API_URL}?email=${encodeURIComponent(normalizedEmail)}`;

  // console.log(`[GoogleSheetsService] Consultando API Gateway: ${requestUrl}`);

  try {
    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API responded with status ${response.status}: ${response.statusText}`);
    }

    const responseText = await response.text();
    // console.log(`[GoogleSheetsService] Respuesta del API: ${responseText}`);

    const responseData: StudentGroupResponse = JSON.parse(responseText);

    if (responseData && responseData.grupo) {
      const grupo = responseData.grupo.trim() || 'General';
      // Guardar en cache
      grupoCache.set(normalizedEmail, grupo);
      // console.log(`[GoogleSheetsService] Grupo obtenido desde API: ${grupo}`);
      return grupo;
    } else {
      // console.warn('[GoogleSheetsService] Respuesta de API no contiene "grupo". Usando "General".');
      const grupo = 'General';
      grupoCache.set(normalizedEmail, grupo);
      return grupo;
    }
  } catch (error) {
    // console.error(`[GoogleSheetsService] Error en petición API:`, error);
    const grupo = 'General';
    grupoCache.set(normalizedEmail, grupo);
    return grupo;
  }
}

/**
 * Limpia el cache de grupos
 */
export function clearGroupCache(): void {
  grupoCache.clear();
  // console.log('[GoogleSheetsService] Cache de grupos limpiado');
}

/**
 * Limpia el grupo cacheado de un email específico
 */
export function clearGroupCacheForEmail(email: string): void {
  const normalizedEmail = email.trim().toLowerCase();
  grupoCache.delete(normalizedEmail);
  // console.log(`[GoogleSheetsService] Cache limpiado para email: ${normalizedEmail}`);
}

export default {
  getStudentGroup,
  clearGroupCache,
  clearGroupCacheForEmail,
};
